// src/components/Navbar.jsx
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useNavViewModel } from '../viewmodels/useNavViewModel';
// import { useThemeViewModel } from '../viewmodels/useThemeViewModel';
import { Download /*, Sun, Moon */ } from 'lucide-react';
import resumePDF from '../assets/Ismail_Jabiulla_Flutter_Developer_Resume.pdf';

const NAV_ITEMS = [
  { label: 'Home', href: '#home' },
  { label: 'About', href: '#about' },
  { label: 'Skills', href: '#skills' },
  { label: 'Portfolio', href: '#portfolio' },
  { label: 'Case Studies', href: '#case-studies' },
  { label: 'Experience', href: '#experience' },
  { label: 'Publications', href: '#publications' },
  { label: 'Blog', href: '#blog' },
  { label: 'Contact', href: '#contact' },
];

export default function Navbar() {
  const { mobileOpen, scrolled, activeSection, setActiveSection, toggleMobile, closeMobile } = useNavViewModel();
  // const { isDark, toggleTheme } = useThemeViewModel();
  const location = useLocation();
  const navigate = useNavigate();

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = resumePDF;
    link.download = 'Ismail_Jabiulla_Flutter_Developer_Resume.pdf';
    link.click();
  };

  const handleNavClick = (e, href) => {
    closeMobile();
    const targetId = href.replace('#', '');
    if (setActiveSection) {
      setActiveSection(targetId);
    }

    const scrollToTarget = () => {
      const el = document.getElementById(targetId);
      if (el) {
        const yOffset = -70;
        const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    };

    if (location.pathname !== '/') {
      e.preventDefault();
      navigate(`/${href}`);
      setTimeout(scrollToTarget, 150);
    } else {
      e.preventDefault();
      scrollToTarget();
    }
  };

  const isAffix = scrolled || location.pathname !== '/';

  return (
    <nav className={`custom-navbar ${isAffix ? 'affix' : ''}`}>
      <div className="container relative">
        {/* Brand Logo */}
        <Link to="/" className="logo" onClick={closeMobile}>
          Mr. Jabiulla
        </Link>

        {/* Desktop Navigation Links */}
        <ul className="nav hidden md:flex items-center">
          {NAV_ITEMS.map(({ label, href }) => {
            const id = href.replace('#', '');
            const isActive = location.pathname === '/' && activeSection === id;
            return (
              <li key={label} className="item">
                <a
                  href={`/${href}`}
                  onClick={(e) => handleNavClick(e, href)}
                  className={`link ${isActive ? 'active' : ''}`}
                >
                  {label}
                </a>
              </li>
            );
          })}
          <li className="item ml-3">
            <button
              onClick={handleDownload}
              className="btn btn-primary btn-sm rounded"
              title="Download Resume"
            >
              <Download size={14} className="mr-1.5 inline" /> Resume
            </button>
          </li>
          {/* Theme Toggle Button (Commented out - theme is always light)
          <li className="item ml-1">
            <button
              onClick={toggleTheme}
              aria-label="Toggle theme"
              className="w-8 h-8 rounded-full border border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-500 dark:text-gray-300 hover:text-primary transition-all duration-200"
              title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {isDark ? <Sun size={14} /> : <Moon size={14} />}
            </button>
          </li>
          */}
        </ul>

        {/* Right side mobile controls */}
        <div className="flex items-center gap-2 md:hidden">
          {/* Theme Toggle Button (Commented out - theme is always light)
          <button
            onClick={toggleTheme}
            aria-label="Toggle theme"
            className="w-8 h-8 rounded-full border border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-400 hover:text-primary transition-all"
          >
            {isDark ? <Sun size={14} /> : <Moon size={14} />}
          </button>
          */}

          {/* Meyawo Elastic Hamburger */}
          <button
            type="button"
            id="nav-toggle"
            aria-label="Toggle navigation menu"
            onClick={toggleMobile}
            className={`hamburger hamburger--elastic ${mobileOpen ? 'is-active' : ''}`}
          >
            <div className="hamburger-box">
              <div className="hamburger-inner"></div>
            </div>
          </button>
        </div>

        {/* Mobile Dropdown Menu */}
        {mobileOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-white dark:bg-[#171722] shadow-xl border-t border-gray-100 dark:border-[#272738] py-4 px-6 z-50 animate-fadeInUp">
            <ul className="flex flex-col gap-3 list-none p-0 m-0">
              {NAV_ITEMS.map(({ label, href }) => {
                const id = href.replace('#', '');
                const isActive = location.pathname === '/' && activeSection === id;
                return (
                  <li key={label} className="border-b border-gray-100 dark:border-[#272738] pb-2">
                    <a
                      href={`/${href}`}
                      onClick={(e) => handleNavClick(e, href)}
                      className={`block font-heading text-lg ${
                        isActive
                          ? 'text-primary font-bold'
                          : 'text-gray-700 dark:text-gray-200 hover:text-primary'
                      }`}
                    >
                      {label}
                    </a>
                  </li>
                );
              })}
              <li className="pt-2 flex justify-between items-center">
                <button
                  onClick={() => {
                    closeMobile();
                    handleDownload();
                  }}
                  className="btn btn-primary btn-sm rounded w-full"
                >
                  <Download size={14} className="mr-1.5 inline" /> Download Resume
                </button>
              </li>
            </ul>
          </div>
        )}
      </div>
    </nav>
  );
}
